<section class="session-records-page">
  <div class="page-header">
    <div>
      <p class="eyebrow">Hotspot sessions</p>
      <h2>{{ getPageTitle() }}</h2>
      <p>{{ getPageDescription() }}</p>
    </div>
    <div class="context-note" *ngIf="isAdminOrPlatform">
      <i class="material-icons">hub</i>
      <span>Unmapped NAS identifiers are expected while asset mappings are still being reviewed.</span>
    </div>
  </div>

  <div class="records-layout">
    <section class="records-panel">
      <div class="panel-toolbar">
        <div>
          <p class="eyebrow">Records</p>
          <h3>{{ getPageSummary() }}</h3>
        </div>
        <div class="filter-row">
          <label class="search-box">
            <i class="material-icons">search</i>
            <input type="search" placeholder="Search passenger, phone, session, NAS" [(ngModel)]="searchTerm" (keyup.enter)="applyFilters()">
          </label>
          <input class="filter-input" type="text" placeholder="Provider" [(ngModel)]="providerFilter" (keyup.enter)="applyFilters()">
          <input class="filter-input" type="text" placeholder="NAS identifier" [(ngModel)]="nasIdentifierFilter" (keyup.enter)="applyFilters()">
          <input class="filter-input short" type="number" placeholder="Profile ID" [(ngModel)]="passengerProfileIDFilter" (keyup.enter)="applyFilters()">
          <select class="filter-input" [(ngModel)]="organisationIDFilter" *ngIf="isAdminOrPlatform" (ngModelChange)="applyFilters()">
            <option [ngValue]="null">All operators</option>
            <option *ngFor="let organisation of organisations" [ngValue]="organisation.organisationID">
              {{ organisation.organisationName || organisation.orgName || ('Organisation ' + organisation.organisationID) }}
            </option>
          </select>
          <select class="filter-input" [(ngModel)]="vehicleIDFilter" (ngModelChange)="applyFilters()">
            <option [ngValue]="null">All buses</option>
            <option *ngFor="let vehicle of vehicles; trackBy: trackVehicle" [ngValue]="vehicle.vehicleID">
              {{ vehicle.vehicleRegistration || vehicle.vehicleIdentificationNumber || vehicle.vehicleLabel || ('Vehicle ' + vehicle.vehicleID) }}
            </option>
          </select>
          <select class="filter-input" [(ngModel)]="activeFilter" (ngModelChange)="applyFilters()">
            <option value="all">All states</option>
            <option value="active">Active</option>
            <option value="ended">Ended</option>
          </select>
          <input class="filter-input" type="date" [(ngModel)]="fromFilter" (ngModelChange)="applyFilters()">
          <input class="filter-input" type="date" [(ngModel)]="toFilter" (ngModelChange)="applyFilters()">
          <button class="filter-action" type="button" (click)="applyFilters()">
            <i class="material-icons">filter_alt</i>
            <span>Apply</span>
          </button>
        </div>
      </div>

      <div class="inline-error" *ngIf="errorMessage">{{ errorMessage }}</div>

      <div class="loading-state" *ngIf="isLoading">
        <i class="material-icons">sync</i>
        <span>Loading hotspot session records...</span>
      </div>

      <div class="table-scroll" *ngIf="!isLoading && records.length; else noRecords">
        <table>
          <thead>
            <tr>
              <th>
                <button class="sort-header" type="button" (click)="sortBy('passenger')" [attr.aria-label]="getSortLabel('passenger') + ' by passenger'">
                  <span>Passenger</span>
                  <i class="material-icons">{{ getSortIcon('passenger') }}</i>
                </button>
              </th>
              <th>
                <button class="sort-header" type="button" (click)="sortBy('phone')" [attr.aria-label]="getSortLabel('phone') + ' by phone'">
                  <span>Phone</span>
                  <i class="material-icons">{{ getSortIcon('phone') }}</i>
                </button>
              </th>
              <th>
                <button class="sort-header" type="button" (click)="sortBy('bus')" [attr.aria-label]="getSortLabel('bus') + ' by bus'">
                  <span>Bus</span>
                  <i class="material-icons">{{ getSortIcon('bus') }}</i>
                </button>
              </th>
              <th *ngIf="isAdminOrPlatform">
                <button class="sort-header" type="button" (click)="sortBy('operator')" [attr.aria-label]="getSortLabel('operator') + ' by operator'">
                  <span>Operator</span>
                  <i class="material-icons">{{ getSortIcon('operator') }}</i>
                </button>
              </th>
              <th>
                <button class="sort-header" type="button" (click)="sortBy('nasIdentifier')" [attr.aria-label]="getSortLabel('nasIdentifier') + ' by NAS identifier'">
                  <span>NAS identifier</span>
                  <i class="material-icons">{{ getSortIcon('nasIdentifier') }}</i>
                </button>
              </th>
              <th>
                <button class="sort-header" type="button" (click)="sortBy('startDateTime')" [attr.aria-label]="getSortLabel('startDateTime') + ' by start time'">
                  <span>Start/end</span>
                  <i class="material-icons">{{ getSortIcon('startDateTime') }}</i>
                </button>
              </th>
              <th>
                <button class="sort-header" type="button" (click)="sortBy('active')" [attr.aria-label]="getSortLabel('active') + ' by state'">
                  <span>State</span>
                  <i class="material-icons">{{ getSortIcon('active') }}</i>
                </button>
              </th>
              <th>
                <button class="sort-header" type="button" (click)="sortBy('dataUsage')" [attr.aria-label]="getSortLabel('dataUsage') + ' by data usage'">
                  <span>Data usage</span>
                  <i class="material-icons">{{ getSortIcon('dataUsage') }}</i>
                </button>
              </th>
              <th>
                <button class="sort-header" type="button" (click)="sortBy('dataPlan')" [attr.aria-label]="getSortLabel('dataPlan') + ' by plan'">
                  <span>Plan</span>
                  <i class="material-icons">{{ getSortIcon('dataPlan') }}</i>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let record of sortedRecords; trackBy: trackRecord"
                [class.selected]="selectedRecord?.hotspotSessionRecordID === record.hotspotSessionRecordID"
                [class.data-quality]="!record.passengerProfileID || !record.vehicleID"
                (click)="selectRecord(record)">
              <td>
                <strong>{{ getPassengerName(record) }}</strong>
                <a *ngIf="record.passengerProfileID" [routerLink]="['/dashboard/passengers/profiles', record.passengerProfileID]" (click)="$event.stopPropagation()">
                  Profile {{ record.passengerProfileID }}
                </a>
                <small *ngIf="!record.passengerProfileID">No local profile linked</small>
              </td>
              <td>{{ getPhone(record) }}</td>
              <td>
                <strong>{{ getBusLabel(record) }}</strong>
                <small>ID {{ record.vehicleID || '-' }}</small>
              </td>
              <td *ngIf="isAdminOrPlatform">
                <strong>{{ record.organisationName || 'Unmapped operator' }}</strong>
                <small>ID {{ record.organisationID || '-' }}</small>
              </td>
              <td>
                <strong>{{ record.nasIdentifier || '-' }}</strong>
                <small>{{ record.provider }}</small>
              </td>
              <td>
                <span>{{ formatDate(record.startDateTime) }}</span>
                <small>{{ formatDate(record.endDateTime) }}</small>
              </td>
              <td>
                <span class="status-badge" [class.active]="record.active" [class.ended]="!record.active">
                  {{ record.active ? 'active' : 'ended' }}
                </span>
                <span class="status-badge quality" *ngIf="!record.passengerProfileID">unlinked</span>
                <span class="status-badge quality" *ngIf="!record.vehicleID">unmapped</span>
              </td>
              <td>{{ getDataUsage(record) }}</td>
              <td>{{ record.dataPlan || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <ng-template #noRecords>
        <div class="empty-state" *ngIf="!isLoading && !hasActiveFilters()">
          <i class="material-icons">wifi_tethering_off</i>
          <h3>No hotspot session records yet</h3>
          <p>Synced local hotspot sessions will appear here after session sync runs create records.</p>
        </div>
        <div class="empty-state" *ngIf="!isLoading && hasActiveFilters()">
          <i class="material-icons">filter_alt_off</i>
          <h3>No sessions match the filters</h3>
          <p>Try clearing search, date, active state, bus, or provider filters.</p>
          <button class="filter-action" type="button" (click)="clearFilters()">
            <i class="material-icons">close</i>
            <span>Clear filters</span>
          </button>
        </div>
      </ng-template>

      <div class="pagination-bar" *ngIf="!isLoading && totalPages > 1">
        <button class="secondary-action" type="button" [disabled]="page === 1" (click)="changePage(page - 1)">
          <i class="material-icons">chevron_left</i>
          <span>Previous</span>
        </button>
        <span>Page {{ page }} of {{ totalPages }}</span>
        <button class="secondary-action" type="button" [disabled]="page === totalPages" (click)="changePage(page + 1)">
          <span>Next</span>
          <i class="material-icons">chevron_right</i>
        </button>
      </div>
    </section>

    <aside class="detail-panel" *ngIf="isLoadingDetail">
      <div class="empty-state">
        <i class="material-icons">sync</i>
        <h3>Loading session</h3>
        <p>Fetching the selected hotspot session record.</p>
      </div>
    </aside>

    <aside class="detail-panel" *ngIf="!isLoadingDetail && selectedRecord">
      <div class="detail-header">
        <div>
          <p class="eyebrow">Session detail</p>
          <h3>{{ selectedRecord.sessionID || selectedRecord.externalID || ('Record ' + selectedRecord.hotspotSessionRecordID) }}</h3>
          <span class="record-id">Record {{ selectedRecord.hotspotSessionRecordID }}</span>
        </div>
        <span class="status-badge" [class.active]="selectedRecord.active" [class.ended]="!selectedRecord.active">
          {{ selectedRecord.active ? 'active' : 'ended' }}
        </span>
      </div>

      <div class="inline-error detail-error" *ngIf="detailError">{{ detailError }}</div>

      <dl class="detail-grid">
        <div>
          <dt>Passenger</dt>
          <dd>{{ getPassengerName(selectedRecord) }}</dd>
        </div>
        <div>
          <dt>Phone</dt>
          <dd>{{ getPhone(selectedRecord) }}</dd>
        </div>
        <div>
          <dt>Profile</dt>
          <dd>
            <a *ngIf="selectedRecord.passengerProfileID" [routerLink]="['/dashboard/passengers/profiles', selectedRecord.passengerProfileID]">
              {{ selectedRecord.passengerProfileID }}
            </a>
            <span *ngIf="!selectedRecord.passengerProfileID">Unlinked passenger</span>
          </dd>
        </div>
        <div>
          <dt>NAS identifier</dt>
          <dd>{{ selectedRecord.nasIdentifier || '-' }}</dd>
        </div>
        <div>
          <dt>Operator</dt>
          <dd>{{ selectedRecord.organisationName || 'Unmapped operator' }}</dd>
        </div>
        <div>
          <dt>Bus</dt>
          <dd>{{ getBusLabel(selectedRecord) }}</dd>
        </div>
        <div>
          <dt>Started</dt>
          <dd>{{ formatDate(selectedRecord.startDateTime) }}</dd>
        </div>
        <div>
          <dt>Ended</dt>
          <dd>{{ formatDate(selectedRecord.endDateTime) }}</dd>
        </div>
        <div>
          <dt>Data in</dt>
          <dd>{{ selectedRecord.dataIn ?? '-' }}</dd>
        </div>
        <div>
          <dt>Data out</dt>
          <dd>{{ selectedRecord.dataOut ?? '-' }}</dd>
        </div>
        <div>
          <dt>Data plan</dt>
          <dd>{{ selectedRecord.dataPlan || '-' }}</dd>
        </div>
        <div>
          <dt>Termination</dt>
          <dd>{{ selectedRecord.terminationCause || '-' }}</dd>
        </div>
      </dl>
    </aside>

    <aside class="detail-panel" *ngIf="!isLoadingDetail && !selectedRecord">
      <div class="empty-state">
        <i class="material-icons">person_search</i>
        <h3>No session selected</h3>
        <p>Select a hotspot session to inspect passenger, bus, NAS, and data usage details.</p>
      </div>
    </aside>
  </div>
</section>
